'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import {
  addTomVendingPurchaseOrderLine,
  cancelTomVendingPurchaseOrder,
  closeShortTomVendingPurchaseOrder,
  createAndPostTomVendingReceipt,
  deleteTomVendingPurchaseOrderLine,
  fetchTomVendingLastVendorUnitCost,
  fetchTomVendingPurchaseOrder,
  fetchTomVendingPoSourceImageUrl,
  submitTomVendingPurchaseOrder,
  updateTomVendingPurchaseOrder,
  useTomVendingVendorsList,
  type TvPurchaseOrder,
} from '@/hooks/useTomVendingPurchasing';
import { tomVendingReceiveAtLocations, useTomVendingLocationsList } from '@/hooks/useTomVendingLocation';
import { useNotification } from '@/contexts/NotificationContext';
import CatalogItemAutocomplete, { type CatalogItemOption } from '../purchasing/CatalogItemAutocomplete';
import { searchTomVendingCatalogItems } from '../purchasing/catalogSearch';
import { formatVendorUnitCostDollars, vendorUnitCostHelperText } from '../purchasing/vendorUnitCostHint';
import type { VendorItemUnitCostHint } from '../purchasing/vendorUnitCostHint';

function normalizeUpcDigits(raw: string): string {
  let digits = String(raw || '').replace(/\D/g, '');
  if (digits.length > 13) digits = digits.slice(-13);
  if (digits.length === 12) return `0${digits}`;
  return digits;
}

function gs1CheckDigit(bodyWithoutCheck: string): string {
  const body = String(bodyWithoutCheck || '').replace(/\D/g, '');
  if (!body) return '';
  let sum = 0;
  for (let i = 0; i < body.length; i++) {
    const n = body.charCodeAt(body.length - 1 - i) - 48;
    sum += n * (i % 2 === 0 ? 3 : 1);
  }
  return String((10 - (sum % 10)) % 10);
}

function hasValidGs1CheckDigit(full: string): boolean {
  const d = String(full || '').replace(/\D/g, '');
  if (![8, 12, 13, 14].includes(d.length)) return false;
  if (gs1CheckDigit(d.slice(0, -1)) === d.slice(-1)) return true;
  if (d.length === 13 && d.startsWith('0')) {
    const upcA = d.slice(1);
    return gs1CheckDigit(upcA.slice(0, -1)) === upcA.slice(-1);
  }
  return false;
}

function toGtin14(raw: string): string {
  let digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length > 14) digits = digits.slice(-14);
  return digits.padStart(14, '0');
}

function expandUpcEToUpcA(raw: string): string | null {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length !== 8) return null;
  const ns = d[0];
  if (ns !== '0' && ns !== '1') return null;
  const d1 = d[1];
  const d2 = d[2];
  const d3 = d[3];
  const d4 = d[4];
  const d5 = d[5];
  const d6 = d[6];
  const check = d[7];
  let body11: string;
  if (d6 === '0' || d6 === '1' || d6 === '2') {
    body11 = `${ns}${d1}${d2}${d6}0000${d3}${d4}${d5}`;
  } else if (d6 === '3') {
    body11 = `${ns}${d1}${d2}${d3}00000${d4}${d5}`;
  } else if (d6 === '4') {
    body11 = `${ns}${d1}${d2}${d3}${d4}00000${d5}`;
  } else {
    body11 = `${ns}${d1}${d2}${d3}${d4}${d5}0000${d6}`;
  }
  const expected = gs1CheckDigit(body11);
  if (expected !== check) return null;
  return body11 + expected;
}

function compressUpcAToUpcE(raw: string): string | null {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('0')) d = d.slice(1);
  if (d.length !== 12) return null;
  if (!hasValidGs1CheckDigit(d)) return null;
  const ns = d[0];
  if (ns !== '0' && ns !== '1') return null;
  const b = d.slice(0, 11);
  let data6: string | null = null;
  if (/^\d[0-9]{2}[0-2]0000\d{3}$/.test(b)) {
    data6 = `${b[1]}${b[2]}${b[8]}${b[9]}${b[10]}${b[3]}`;
  } else if (/^\d[0-9]{3}00000\d{2}$/.test(b)) {
    data6 = `${b[1]}${b[2]}${b[3]}${b[9]}${b[10]}3`;
  } else if (/^\d[0-9]{4}00000\d$/.test(b)) {
    data6 = `${b[1]}${b[2]}${b[3]}${b[4]}${b[10]}4`;
  } else if (/^\d[0-9]{5}0000[5-9]$/.test(b)) {
    data6 = `${b[1]}${b[2]}${b[3]}${b[4]}${b[5]}${b[10]}`;
  }
  if (!data6 || data6.length !== 6) return null;
  const upcE = `${ns}${data6}${d[11]}`;
  if (expandUpcEToUpcA(upcE) !== d) return null;
  return upcE;
}

/** Mirror of server `gtinLookupCandidates` (GTIN-14 + validated check neighbors + UPC-E). */
function upcCandidates(raw: string): string[] {
  const input = String(raw || '').replace(/\D/g, '');
  if (!input) return [];
  const out: string[] = [];
  const add = (s: string) => {
    if (s.length > 0 && !out.includes(s)) out.push(s);
  };
  const canonical = normalizeUpcDigits(input);
  add(canonical);
  const identities = new Set<string>();
  const addId = (s: string) => {
    const g = toGtin14(s);
    if (g) identities.add(g);
  };
  addId(input);
  addId(canonical);
  const considerCheckNeighbors = (full: string) => {
    if (!full) return;
    if (hasValidGs1CheckDigit(full)) {
      const body = full.slice(0, -1);
      addId(body);
      add(body);
      if (full.length === 13 && full.startsWith('0') && hasValidGs1CheckDigit(full.slice(1))) {
        const upcBody = full.slice(1, -1);
        addId(upcBody);
        add(upcBody);
      }
    }
    if ((full.length === 11 || full.length === 12) && !hasValidGs1CheckDigit(full)) {
      const withCheck = full + gs1CheckDigit(full);
      addId(withCheck);
      add(normalizeUpcDigits(withCheck));
    }
  };
  considerCheckNeighbors(input);
  considerCheckNeighbors(canonical);
  if (canonical.length === 13 && canonical.startsWith('0')) {
    considerCheckNeighbors(canonical.slice(1));
  }
  const expanded = expandUpcEToUpcA(input);
  if (expanded) {
    add(expanded);
    add(normalizeUpcDigits(expanded));
    addId(expanded);
    considerCheckNeighbors(expanded);
  }
  const compressed =
    compressUpcAToUpcE(input) ??
    compressUpcAToUpcE(canonical) ??
    (canonical.length === 13 && canonical.startsWith('0') ? compressUpcAToUpcE(canonical.slice(1)) : null);
  if (compressed) {
    add(compressed);
    addId(compressed);
  }
  for (const g14 of identities) {
    add(g14.slice(-13));
    add(g14.slice(-12));
    add(g14);
    const significant = g14.replace(/^0+/, '') || '0';
    add(normalizeUpcDigits(significant));
    add(normalizeUpcDigits(g14));
    add(significant);
  }
  for (const c of [...out]) {
    if (c.length === 12) add(`0${c}`);
    else if (c.length === 13 && c.startsWith('0')) add(c.slice(1));
  }
  return out;
}

function lineMatchesUpc(
  line: NonNullable<TvPurchaseOrder['lines']>[number],
  scanned: string,
): boolean {
  const candidates = new Set(upcCandidates(scanned));
  const itemUpc = line.inventoryItem?.upc ?? '';
  for (const c of upcCandidates(itemUpc)) {
    if (candidates.has(c)) return true;
  }
  // GTIN-14 identity equality (leading zeros)
  if (toGtin14(scanned) && toGtin14(scanned) === toGtin14(itemUpc)) return true;
  return false;
}

function openQty(line: NonNullable<TvPurchaseOrder['lines']>[number]): number {
  return Math.max(0, line.orderedQty - line.receivedQty - (line.closedShortQty ?? 0));
}

/** Received vs not-received report row (computed from PO lines). */
function receiveReportRows(po: TvPurchaseOrder) {
  return (po.lines ?? []).map((line) => {
    const notReceived = Math.max(0, line.orderedQty - line.receivedQty);
    return {
      line,
      ordered: line.orderedQty,
      received: line.receivedQty,
      notReceived,
      closedShort: line.closedShortQty ?? 0,
    };
  });
}

const TomsVendingPurchaseOrderDetailPage: React.FC = () => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { data: locations = [] } = useTomVendingLocationsList();
  const { data: vendors = [] } = useTomVendingVendorsList('', false);
  const [po, setPo] = useState<TvPurchaseOrder | null>(null);
  const [headerModalOpen, setHeaderModalOpen] = useState(false);
  const [receiveQty, setReceiveQty] = useState<Record<number, string>>({});
  const [receiveCost, setReceiveCost] = useState<Record<number, string>>({});
  const [receiptNumber, setReceiptNumber] = useState('');
  const [selectedCatalogItem, setSelectedCatalogItem] = useState<CatalogItemOption | null>(null);
  const [newQty, setNewQty] = useState('1');
  const [newCost, setNewCost] = useState('');
  const [addLineCostHint, setAddLineCostHint] = useState<VendorItemUnitCostHint | null>(null);
  const [scanBuffer, setScanBuffer] = useState('');
  const [scanFlashLineId, setScanFlashLineId] = useState<number | null>(null);
  const [closeRemaining, setCloseRemaining] = useState(true);
  const [closeReason, setCloseReason] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportPo, setReportPo] = useState<TvPurchaseOrder | null>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const id = parseInt(poId ?? '', 10);
    if (Number.isNaN(id)) return;
    try {
      const data = await fetchTomVendingPurchaseOrder(id);
      setPo(data);
      const q: Record<number, string> = {};
      const c: Record<number, string> = {};
      for (const line of data.lines ?? []) {
        q[line.id] = '0';
        c[line.id] = line.unitCostCents != null ? String(line.unitCostCents / 100) : '';
      }
      setReceiveQty(q);
      setReceiveCost(c);
      setReceiptNumber(`GR-${data.poNumber}-${Date.now()}`);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load PO');
    }
  }, [poId, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const vendorId = po?.vendor?.id;
    if (!selectedCatalogItem || !vendorId) {
      setAddLineCostHint(null);
      return;
    }
    let cancelled = false;
    void fetchTomVendingLastVendorUnitCost(vendorId, selectedCatalogItem.id).then((hint) => {
      if (cancelled) return;
      setAddLineCostHint(hint);
      if (hint.unitCostCents != null) {
        setNewCost(formatVendorUnitCostDollars(hint.unitCostCents));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedCatalogItem, po?.vendor?.id]);

  const canReceive = !!po && po.status !== 'cancelled' && po.status !== 'received' && po.status !== 'draft';

  useEffect(() => {
    if (!canReceive) return;
    const t = window.setTimeout(() => scanInputRef.current?.focus(), 200);
    return () => window.clearTimeout(t);
  }, [canReceive, po?.id]);

  const headerFieldGroups = useMemo(
    () => {
      const receiveAtLocations = tomVendingReceiveAtLocations(locations, po?.receiveLocation?.id);
      return [
      {
        groupName: 'Header',
        fields: [
          {
            label: 'Vendor',
            name: 'vendorId',
            type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
            required: true,
            columns: 12,
            options: vendors.map((v) => ({ label: v.name, value: v.id })),
          },
          ...(receiveAtLocations.length === 1
            ? []
            : [
                {
                  label: 'Receive at location',
                  name: 'receiveLocationId',
                  type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
                  required: true,
                  columns: 12,
                  options: receiveAtLocations.map((l) => ({
                    label: l.name ?? `Location ${l.id}`,
                    value: l.id,
                  })),
                },
              ]),
          { label: 'PO number', name: 'poNumber', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
          { label: 'Notes', name: 'notes', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
        ],
      },
    ];
    },
    [vendors, locations, po?.receiveLocation?.id],
  );

  const applyScan = (raw: string) => {
    if (!po) return;
    const code = raw.trim();
    if (!code) return;
    const match = (po.lines ?? []).find((line) => lineMatchesUpc(line, code));
    if (!match) {
      showError(`UPC ${code} is not on this PO`);
      return;
    }
    setReceiveQty((prev) => {
      const current = parseInt(prev[match.id] ?? '0', 10) || 0;
      const next = current + 1;
      const max = openQty(match);
      if (next > max) {
        showError(`Cannot receive more than remaining (${max}) for ${match.inventoryItem?.upc}`);
        return prev;
      }
      return { ...prev, [match.id]: String(next) };
    });
    setScanFlashLineId(match.id);
    window.setTimeout(() => setScanFlashLineId(null), 600);
    const still = Math.max(0, openQty(match) - ((parseInt(receiveQty[match.id] ?? '0', 10) || 0) + 1));
    showSuccess(
      `Scanned ${match.inventoryItem?.name || match.inventoryItem?.upc} — still to scan: ${still}`,
    );
  };

  const handleScanKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyScan(scanBuffer);
      setScanBuffer('');
    }
  };

  const varianceRows = useMemo(() => {
    if (!po) return [];
    return (po.lines ?? []).map((line) => {
      const thisScan = parseInt(receiveQty[line.id] ?? '0', 10) || 0;
      const remainingAfter = Math.max(0, openQty(line) - thisScan);
      return {
        line,
        thisScan,
        remainingAfter,
        over: thisScan > openQty(line),
        short: remainingAfter > 0 && thisScan >= 0,
      };
    });
  }, [po, receiveQty]);

  const handleSubmit = async () => {
    if (!po) return;
    if ((po.lines ?? []).length === 0) {
      showError('Add at least one line before submitting');
      return;
    }
    try {
      await submitTomVendingPurchaseOrder(po.id);
      showSuccess('PO submitted');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Submit failed');
    }
  };

  const handleCancelPo = async () => {
    if (!po || !window.confirm(`Cancel purchase order "${po.poNumber}"?`)) return;
    try {
      await cancelTomVendingPurchaseOrder(po.id);
      showSuccess('Purchase order cancelled');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Cancel failed');
    }
  };

  const handleReceive = async () => {
    if (!po) return;
    const over = varianceRows.find((r) => r.over);
    if (over) {
      showError('Cannot over-receive — reduce qty on lines that exceed remaining');
      return;
    }
    const lines = (po.lines ?? [])
      .map((line) => {
        const receivedQty = parseInt(receiveQty[line.id] ?? '0', 10);
        const dollars = parseFloat(receiveCost[line.id] ?? '0');
        if (receivedQty <= 0) return null;
        return {
          purchaseOrderLineId: line.id,
          receivedQty,
          unitCostCents: Math.round(dollars * 100),
        };
      })
      .filter(Boolean) as Array<{ purchaseOrderLineId: number; receivedQty: number; unitCostCents: number }>;
    if (lines.length === 0 && !closeRemaining) {
      showError('Enter receive qty on at least one line, or zero remaining for next service');
      return;
    }
    try {
      if (lines.length > 0) {
        await createAndPostTomVendingReceipt({
          purchaseOrderId: po.id,
          receiptNumber: receiptNumber.trim(),
          lines,
        });
      }
      if (closeRemaining) {
        await closeShortTomVendingPurchaseOrder({
          id: po.id,
          reason: closeReason.trim() || 'Zero remaining for next service after scan receive',
        });
      }
      const refreshed = await fetchTomVendingPurchaseOrder(po.id);
      setPo(refreshed);
      const q: Record<number, string> = {};
      const c: Record<number, string> = {};
      for (const line of refreshed.lines ?? []) {
        q[line.id] = '0';
        c[line.id] = line.unitCostCents != null ? String(line.unitCostCents / 100) : '';
      }
      setReceiveQty(q);
      setReceiveCost(c);
      setReceiptNumber(`GR-${refreshed.poNumber}-${Date.now()}`);
      setReportPo(refreshed);
      setReportOpen(true);
      showSuccess(
        closeRemaining
          ? 'Receive complete — order zeroed for next service'
          : 'Receipt posted — inventory updated',
      );
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Receive failed');
    }
  };

  const handleAddLine = async () => {
    if (!po) return;
    if (!selectedCatalogItem) {
      showError('Select a catalog item');
      return;
    }
    try {
      await addTomVendingPurchaseOrderLine({
        purchaseOrderId: po.id,
        inventoryItemId: selectedCatalogItem.id,
        orderedQty: parseInt(newQty, 10) || 1,
        unitCostCents: newCost ? Math.round(parseFloat(newCost) * 100) : undefined,
        lineType: 'sku',
      });
      showSuccess('Line added');
      setSelectedCatalogItem(null);
      setAddLineCostHint(null);
      setNewQty('1');
      setNewCost('');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to add line');
    }
  };

  const handleDeleteLine = async (lineId: number) => {
    if (!window.confirm('Remove this line?')) return;
    try {
      await deleteTomVendingPurchaseOrderLine(lineId);
      showSuccess('Line removed');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to remove line');
    }
  };

  if (!po) {
    return <Typography sx={{ p: 2 }}>Loading…</Typography>;
  }

  const isDraft = po.status === 'draft';
  const isCancelled = po.status === 'cancelled';
  const canReceiveUi = !isCancelled && po.status !== 'received';

  return (
    <>
      {isDraft && (
        <GenericFormModal
          visible={headerModalOpen}
          onDismiss={() => setHeaderModalOpen(false)}
          onSubmit={async (values: Record<string, unknown>) => {
            try {
              await updateTomVendingPurchaseOrder({
                id: po.id,
                vendorId: Number(values.vendorId),
                receiveLocationId: Number(
                  values.receiveLocationId ?? po.receiveLocation?.id ?? '',
                ),
                poNumber: String(values.poNumber ?? '').trim(),
                notes: String(values.notes ?? '').trim() || null,
              });
              showSuccess('Header updated');
              setHeaderModalOpen(false);
              await load();
            } catch (e: unknown) {
              showError(e instanceof Error ? e.message : 'Update failed');
            }
          }}
          initialValues={{
            vendorId: po.vendor?.id ?? '',
            receiveLocationId: po.receiveLocation?.id ?? '',
            poNumber: po.poNumber,
            notes: po.notes ?? '',
          }}
          fieldGroups={headerFieldGroups}
          title="Edit purchase order"
        />
      )}

      <TitleBar
        breadcrumbs={[
          { label: 'Home', href: '/app/dashboard' },
          { label: 'Purchase orders', href: '/app/toms-vending/purchasing/purchase-orders' },
          { label: po.poNumber },
        ]}
        title={po.poNumber}
        rightContent={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isDraft && (
              <Button variant="outlined" onClick={() => setHeaderModalOpen(true)} sx={{ textTransform: 'none' }}>
                Edit header
              </Button>
            )}
            {po.status !== 'cancelled' && po.status !== 'received' && (
              <Button variant="outlined" color="warning" onClick={() => void handleCancelPo()} sx={{ textTransform: 'none' }}>
                Cancel PO
              </Button>
            )}
          </Box>
        }
      />

      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {po.vendor?.name} → {po.receiveLocation?.name} ·{' '}
          <Chip label={po.status.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ ml: 0.5 }} />
          {po.sourceImageS3Key ? (
            <Chip label="Created from photo" size="small" variant="outlined" sx={{ ml: 0.5 }} />
          ) : null}
        </Typography>

        {(po.sourceImageUrl || po.sourceImageS3Key) && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'inline-block' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              PO photo
            </Typography>
            {po.sourceImageUrl ? (
              <Box
                component="a"
                href={po.sourceImageUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'block' }}
              >
                <Box
                  component="img"
                  src={po.sourceImageUrl}
                  alt={`PO ${po.poNumber} photo`}
                  sx={{ maxWidth: 420, maxHeight: 280, objectFit: 'contain', display: 'block' }}
                />
              </Box>
            ) : (
              <Button
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  void fetchTomVendingPoSourceImageUrl(po.id)
                    .then((r) => setPo((prev) => (prev ? { ...prev, sourceImageUrl: r.url } : prev)))
                    .catch((e: unknown) =>
                      showError(e instanceof Error ? e.message : 'Could not load PO photo'),
                    );
                }}
              >
                View PO photo
              </Button>
            )}
          </Paper>
        )}

        {isDraft && (
          <Button variant="contained" onClick={() => void handleSubmit()} sx={{ mb: 2, textTransform: 'none' }}>
            Submit PO
          </Button>
        )}

        {isDraft && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Add line
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <CatalogItemAutocomplete
                value={selectedCatalogItem}
                onChange={setSelectedCatalogItem}
                searchCatalog={searchTomVendingCatalogItems}
                sx={{ minWidth: 320 }}
              />
              <TextField size="small" label="Qty" value={newQty} onChange={(e) => setNewQty(e.target.value)} sx={{ width: 80 }} />
              <TextField
                size="small"
                label="Unit cost ($)"
                value={newCost}
                onChange={(e) => setNewCost(e.target.value)}
                helperText={vendorUnitCostHelperText(addLineCostHint)}
                sx={{ width: 160 }}
              />
              <Button variant="contained" onClick={() => void handleAddLine()} sx={{ textTransform: 'none' }}>
                Add line
              </Button>
            </Box>
          </Paper>
        )}

        {canReceive && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Scan warehouse inbound
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Expected on this order (cloud) is subtracted as you scan. Each beep adds +1 to scanned
              this session.
            </Typography>
            <TextField
              inputRef={scanInputRef}
              size="small"
              label="Scan UPC"
              value={scanBuffer}
              onChange={(e) => setScanBuffer(e.target.value)}
              onKeyDown={handleScanKeyDown}
              helperText="Focus this field and scan with a USB barcode / HID wedge"
              sx={{ maxWidth: 420 }}
              autoComplete="off"
            />
          </Paper>
        )}

        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Expected (cloud)</TableCell>
              <TableCell align="right">Already received</TableCell>
              <TableCell align="right">Zeroed short</TableCell>
              <TableCell align="right">Still to scan</TableCell>
              {canReceiveUi && <TableCell align="right">Scanned this session</TableCell>}
              {canReceiveUi && <TableCell align="right">Unit cost ($)</TableCell>}
              {isDraft && <TableCell width={48} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {(po.lines ?? []).map((line) => (
              <TableRow
                key={line.id}
                sx={{ bgcolor: scanFlashLineId === line.id ? 'success.light' : undefined }}
              >
                <TableCell>
                  {line.inventoryItem?.upc} {line.inventoryItem?.name ? `— ${line.inventoryItem.name}` : ''}
                </TableCell>
                <TableCell align="right">{line.orderedQty}</TableCell>
                <TableCell align="right">{line.receivedQty}</TableCell>
                <TableCell align="right">{line.closedShortQty ?? 0}</TableCell>
                <TableCell align="right">{openQty(line)}</TableCell>
                {canReceiveUi && (
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={receiveQty[line.id] ?? ''}
                      onChange={(e) => setReceiveQty((prev) => ({ ...prev, [line.id]: e.target.value }))}
                      sx={{ width: 80 }}
                      disabled={po.status === 'draft'}
                    />
                  </TableCell>
                )}
                {canReceiveUi && (
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={receiveCost[line.id] ?? ''}
                      onChange={(e) => setReceiveCost((prev) => ({ ...prev, [line.id]: e.target.value }))}
                      sx={{ width: 100 }}
                      disabled={po.status === 'draft'}
                    />
                  </TableCell>
                )}
                {isDraft && (
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label="Remove line"
                      disabled={line.receivedQty > 0}
                      onClick={() => void handleDeleteLine(line.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {canReceive && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Session check before post
            </Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Expected</TableCell>
                  <TableCell align="right">Already recv</TableCell>
                  <TableCell align="right">Scanned this session</TableCell>
                  <TableCell align="right">Still to scan after post</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {varianceRows.map(({ line, thisScan, remainingAfter, over }) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      {line.inventoryItem?.upc}
                      {line.inventoryItem?.name ? ` — ${line.inventoryItem.name}` : ''}
                    </TableCell>
                    <TableCell align="right">{line.orderedQty}</TableCell>
                    <TableCell align="right">{line.receivedQty}</TableCell>
                    <TableCell align="right">{thisScan}</TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={remainingAfter}
                        color={over ? 'error' : remainingAfter > 0 ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {varianceRows.some((r) => r.remainingAfter > 0) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Some lines will still be open after this receipt. Check “Zero remaining for next
                service” so the cloud order is cleared without inventoring phantom stock.
              </Alert>
            )}
            <FormControlLabel
              control={
                <Checkbox checked={closeRemaining} onChange={(e) => setCloseRemaining(e.target.checked)} />
              }
              label="Zero remaining for next service"
            />
            {closeRemaining && (
              <TextField
                size="small"
                label="Zero remaining note"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                sx={{ display: 'block', mb: 2, maxWidth: 420 }}
              />
            )}
            <TextField
              size="small"
              label="Receipt number"
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value)}
              sx={{ mb: 2, display: 'block', maxWidth: 360 }}
            />
            <Button variant="contained" onClick={() => void handleReceive()} sx={{ textTransform: 'none' }}>
              Complete receive and show report
            </Button>
          </Paper>
        )}

        {(po.status === 'received' || (po.lines ?? []).some((l) => l.receivedQty > 0 || (l.closedShortQty ?? 0) > 0)) && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Receive report (Received vs Not received)</Typography>
              <Button
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  setReportPo(po);
                  setReportOpen(true);
                }}
              >
                Open report
              </Button>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Ordered</TableCell>
                  <TableCell align="right">Received</TableCell>
                  <TableCell align="right">Not received</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receiveReportRows(po).map(({ line, ordered, received, notReceived }) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      {line.inventoryItem?.upc}
                      {line.inventoryItem?.name ? ` — ${line.inventoryItem.name}` : ''}
                    </TableCell>
                    <TableCell align="right">{ordered}</TableCell>
                    <TableCell align="right">{received}</TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={notReceived}
                        color={notReceived > 0 ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {po.status === 'draft' && canReceiveUi && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Submit this PO to enable receiving.
          </Typography>
        )}

        <Button sx={{ mt: 2, textTransform: 'none' }} onClick={() => navigate('/app/toms-vending/purchasing/purchase-orders')}>
          ← Back to purchase orders
        </Button>
      </Box>

      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Receive report — Received vs Not received</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {(reportPo ?? po)?.poNumber}
            {(reportPo ?? po)?.status === 'received'
              ? ' — cloud order zeroed for next service'
              : ''}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Ordered</TableCell>
                <TableCell align="right">Received (scanned)</TableCell>
                <TableCell align="right">Not received</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receiveReportRows(reportPo ?? po!).map(({ line, ordered, received, notReceived }) => (
                <TableRow key={line.id}>
                  <TableCell>
                    {line.inventoryItem?.upc}
                    {line.inventoryItem?.name ? ` — ${line.inventoryItem.name}` : ''}
                  </TableCell>
                  <TableCell align="right">{ordered}</TableCell>
                  <TableCell align="right">{received}</TableCell>
                  <TableCell align="right">{notReceived}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)} sx={{ textTransform: 'none' }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TomsVendingPurchaseOrderDetailPage;
